import { useEffect, useMemo } from 'react';
import date from 'date-and-time';

import { usePageTitle } from '../../contexts/PageTitleContext';
import { usePendingActions } from '../../contexts/PendingActionContext';
import { SectionLoading } from '../../components/shared/SectionLoading';
import { ITemplateConfiguration } from '../../types/Template/Template';
import { IAppliance } from '../../types/Appliance/Appliance';
import { Card } from '../base/Card';
import { ReadingsCards } from './ReadingsCards';
import { ReadingsTable } from './ReadingsTable';
import { useReadingsDisplayData } from '../../hooks/useReadingsDisplayData';

interface IReadingsDisplayProps {
  selectedAppliance: IAppliance;
  tableView: boolean;
  superAdminView: boolean;
  selectedTemplate?: ITemplateConfiguration;
}

export function ReadingsDisplay({
  selectedAppliance,
  tableView,
  superAdminView,
  selectedTemplate,
}: IReadingsDisplayProps) {
  const { setSubTitle } = usePageTitle();
  const {
    hardwareId: pendingActionHardwareId,
    setHardware,
    reset,
  } = usePendingActions();

  const {
    dataPoints,
    latest,
    latestTimestamp,
    loading,
    readingUnavailable,
    isLoading,
  } = useReadingsDisplayData(selectedAppliance, selectedTemplate);

  // Memoize filtered datapoints
  const filteredDataPoints = useMemo(() => {
    if (!dataPoints.data?.[0]?.datapoints) return [];
    return dataPoints.data[0].datapoints.filter(
      (dp) => superAdminView || !dp.systemOnly
    );
  }, [dataPoints.data, superAdminView]);

  // Update subtitle when timestamp changes
  useEffect(() => {
    if (loading) {
      setSubTitle('');
    } else if (latestTimestamp) {
      setSubTitle(
        `Last updated: ${date.format(
          new Date(latestTimestamp),
          'DD/MM/YYYY HH:mm:ss'
        )}`
      );
    } else if (!latest) {
      setSubTitle('Last updated: Never');
    }
  }, [setSubTitle, loading, latest, latestTimestamp]);

  // Handle pending actions
  useEffect(() => {
    if (pendingActionHardwareId !== selectedAppliance.id) {
      reset();
    }
    setHardware(selectedAppliance.id);
  }, [selectedAppliance, pendingActionHardwareId, reset, setHardware]);

  if (isLoading || dataPoints.isFetching) {
    return <SectionLoading />;
  }

  if (!readingUnavailable && !latest) {
    return <SectionLoading />;
  }

  if (filteredDataPoints.length === 0) {
    return (
      <Card>
        <div className="h-24 flex items-center justify-center">
          <p>
            All data points are currently hidden. Please use the 'Edit View'
            button to add them.
          </p>
        </div>
      </Card>
    );
  }

  return tableView ? (
    <ReadingsTable dataPoints={filteredDataPoints} />
  ) : (
    <ReadingsCards
      dataPoints={filteredDataPoints}
      selectedAppliance={selectedAppliance}
      timestamp={latest?.timestamp || ''}
    />
  );
}
